import React, { useEffect, useState } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import api from '@/lib/api';
import { Building2, Award, TrendingUp, AlertTriangle, Loader2, RefreshCw } from 'lucide-react';

const VERDE = '#10B981';
const AMBAR = '#F59E0B';
const ROJO = '#DC2626';
const AZUL = '#0369A1';
const GRIS = '#6B7280';

function colorScore(s) {
  if (s == null) return GRIS;
  if (s >= 90) return VERDE;
  if (s >= 70) return AMBAR;
  return ROJO;
}

export default function PortalCorporativo() {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  const cargar = async () => {
    setCargando(true);
    try {
      const r = await api.get('/corporativo/panel');
      setDatos(r.data);
      setError('');
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo cargar el panel');
    } finally { setCargando(false); }
  };

  useEffect(() => { cargar(); }, []);

  const res = datos?.resumen || {};

  return (
    <DashboardLayout>
      <div style={{ maxWidth: 1150, margin: '0 auto', padding: '8px 4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>
              {datos?.grupo || 'Portal corporativo'}
            </h1>
            <p style={{ color: GRIS, fontSize: 14, marginTop: 5, marginBottom: 0 }}>
              Estado de seguridad de todas tus unidades
            </p>
          </div>
          <button onClick={cargar} disabled={cargando}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
              border: '1px solid #DDD', borderRadius: 8, background: '#FFF', cursor: 'pointer' }}>
            <RefreshCw size={15} /> Actualizar
          </button>
        </div>

        {error ? (
          <div style={{ marginTop: 26, padding: 20, border: '1px solid #FCA5A5',
            background: '#FEF2F2', borderRadius: 10, color: '#991B1B' }}>{error}</div>
        ) : cargando ? (
          <div style={{ display: 'flex', gap: 10, color: GRIS, padding: 50 }}>
            <Loader2 size={18} className="animate-spin" /> Cargando...
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 14, margin: '26px 0' }}>
              {[
                { et: 'Unidades', v: res.plazas ?? 0, ic: Building2, c: AZUL },
                { et: 'Promedio del grupo', v: res.promedio_grupo != null ? res.promedio_grupo + '%' : '—',
                  ic: TrendingUp, c: colorScore(res.promedio_grupo) },
                { et: 'Con distintivo', v: `${res.con_distintivo ?? 0} de ${res.plazas ?? 0}`,
                  ic: Award, c: VERDE },
                { et: 'Hallazgos criticos', v: res.hallazgos_criticos ?? 0,
                  ic: AlertTriangle, c: (res.hallazgos_criticos ? ROJO : GRIS) },
              ].map((t, i) => {
                const Ic = t.ic;
                return (
                  <div key={i} style={{ background: '#F8F9FA', border: '1px solid #E5E7EB',
                    borderRadius: 10, padding: '16px 18px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7,
                      color: GRIS, fontSize: 12, marginBottom: 8 }}>
                      <Ic size={14} /> {t.et}
                    </div>
                    <div style={{ fontSize: 23, fontWeight: 700, color: t.c }}>{t.v}</div>
                  </div>
                );
              })}
            </div>

            <h2 style={{ fontSize: 17, fontWeight: 700, margin: '0 0 6px' }}>Tus unidades</h2>
            <p style={{ color: GRIS, fontSize: 13, marginTop: 0, marginBottom: 14 }}>
              Ordenadas por resultado: primero las que requieren atencion.
            </p>

            {!datos?.plazas?.length ? (
              <div style={{ textAlign: 'center', padding: 50, color: GRIS,
                border: '1px dashed #DDD', borderRadius: 12 }}>
                <Building2 size={36} style={{ opacity: 0.35, marginBottom: 10 }} />
                <div style={{ fontWeight: 600 }}>Aun no hay unidades asociadas</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 12 }}>
                {datos.plazas.map((p) => {
                  const col = colorScore(p.ultimo_score);
                  return (
                    <div key={p.client_id} style={{ border: '1px solid #E5E7EB',
                      borderRadius: 12, padding: 17, background: '#FFF',
                      borderLeft: '4px solid ' + col }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between',
                        alignItems: 'flex-start', gap: 14 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: 15.5 }}>{p.nombre}</div>
                          {p.direccion && (
                            <div style={{ color: GRIS, fontSize: 12.5, marginTop: 2 }}>{p.direccion}</div>
                          )}
                          <div style={{ color: GRIS, fontSize: 12.5, marginTop: 7 }}>
                            {p.auditorias} auditoria(s) · {p.aprobadas} aprobada(s)
                            {p.ultima_auditoria && ` · ultima ${p.ultima_auditoria}`}
                          </div>
                          {p.ciclo?.tiene_ciclo && (
                            <div style={{ marginTop: 10 }}>
                              <div style={{ fontSize: 12, color: GRIS, marginBottom: 4 }}>
                                {p.ciclo.aprobadas} de {p.ciclo.requeridas} hacia su distintivo
                              </div>
                              <div style={{ height: 7, background: '#F0F1F3',
                                borderRadius: 5, overflow: 'hidden', maxWidth: 320 }}>
                                <div style={{ height: '100%', borderRadius: 5,
                                  width: `${Math.min(100, p.ciclo.porcentaje)}%`,
                                  background: p.distintivo_vigente ? VERDE : AZUL }} />
                              </div>
                            </div>
                          )}
                        </div>
                        <div style={{ textAlign: 'right', minWidth: 110 }}>
                          <div style={{ fontSize: 27, fontWeight: 700, color: col, lineHeight: 1 }}>
                            {p.ultimo_score != null ? p.ultimo_score + '%' : '—'}
                          </div>
                          <div style={{ fontSize: 11, color: GRIS, marginTop: 4 }}>ultimo resultado</div>
                          {p.distintivo_vigente && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                              marginTop: 9, padding: '4px 10px', borderRadius: 20, fontSize: 11,
                              fontWeight: 600, background: VERDE + '18', color: VERDE }}>
                              <Award size={11} /> Distintivo vigente
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
